import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { ArrowLeft, Download, Filter, Clock, X, SlidersHorizontal, ChevronDown, ChevronUp, Calendar, Type, List } from 'lucide-react'
import axios from '../../../../config/axios'
import {
  StandardDataTable,
  useColumnsState,
  StandardColumn,
} from '../../../../components/StandardPage'

const stepHistoryScreenKey = 'pipeline_step_history'

const PageRoot = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  padding: 0 24px 24px 24px;
`

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
`

const BackButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-weight: 500;
  &:hover { color: #0090D3; }
`

const PageTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #303030;
  margin: 0;
  flex: 1;
`

const ExportButton = styled.button`
  background-color: #0090D3;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover { background-color: #007ab8; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`

const FiltersBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 16px;
`

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FilterLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const FilterInput = styled.input`
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #303030;
  background: #fff;
  &:focus { outline: none; border-color: #0090D3; }
`

const FilterSelect = styled.select`
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #303030;
  background: #fff;
  &:focus { outline: none; border-color: #0090D3; }
`

const ApplyFilterButton = styled.button`
  height: 32px;
  padding: 0 16px;
  background-color: #0090D3;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: flex-end;
  &:hover { background-color: #007ab8; }
`

const ClearFilterButton = styled.button`
  height: 32px;
  padding: 0 12px;
  background: none;
  color: #6b7280;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  align-self: flex-end;
  &:hover { color: #BF383B; border-color: #BF383B; }
`

const TableWrapper = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
`

const ExpiredBadge = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #7F1D1D;
  background-color: #FEE2E2;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 6px;
`

const ReworkTag = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #92400e;
  background-color: #fef3c7;
  padding: 2px 6px;
  border-radius: 4px;
`

const LoadingState = styled.div`
  padding: 40px 0;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`

const DrawerOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  visibility: ${props => props.$isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.2s ease, visibility 0.2s ease;
`

const DrawerContent = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  width: 380px;
  height: 100%;
  background: #ffffff;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  transform: translateX(${props => props.$isOpen ? '0' : '100%'});
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  overflow: hidden;
`

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
`

const DrawerTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #374151;
  margin: 0;
`

const DrawerCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  &:hover {
    background-color: #f3f4f6;
    color: #4b5563;
  }
`

const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const AccordionItem = styled.div`
  border-bottom: 1px solid #f3f4f6;
  padding-bottom: 12px;
`

const AccordionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 15px;
  font-weight: 700;
  color: #0090D3;
  cursor: pointer;
  padding: 8px 0;
`

const AccordionContent = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  gap: 8px;
  padding: 8px 0 8px 4px;
`

const FieldItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-radius: 6px;
  padding: 4px 0;
`

const FieldLabelContainer = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  background: ${props => props.$active ? '#f3f4f6' : 'transparent'};
  transition: background 0.15s ease;
  &:hover {
    background: #f3f4f6;
  }
`

const FieldLabelText = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
  display: flex;
  align-items: center;
  gap: 8px;
`

const FieldFilterContent = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'block' : 'none'};
  padding: 4px 8px 8px 24px;
`

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
`

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #4b5563;
  cursor: pointer;
`

const RadioInput = styled.input`
  cursor: pointer;
  accent-color: #0090D3;
`

const TextInput = styled.input`
  width: 100%;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #303030;
  background: #fff;
  margin-top: 4px;
  &:focus {
    outline: none;
    border-color: #0090D3;
  }
`

const ApplyButton = styled.button`
  height: 36px;
  background-color: #0090D3;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  &:hover { background-color: #007ab8; }
`

const formatDuration = (ms: number) => {
  const seconds = Math.floor(ms / 1000)
  const d = Math.floor(seconds / 86400)
  const h = Math.floor((seconds % 86400) / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (d > 0) return `${d}d ${h}h`
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m`
  return `${seconds}s`
}

const formatDateTime = (iso: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
    ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}

interface HistoryRow {
  id: number
  ticketId: number
  stepId: number
  stepName: string
  enteredAt: string
  exitedAt: string | null
  userId: number | null
  userName: string | null
  durationMs: number
  isCurrent: boolean
  ticket: { id: number; title: string; status: string; deadline: string | null; urgency: boolean; createByName: string | null }
  tags: string[]
  stepExecutionTime: number | null
  fields?: Record<string, string>
}

const StepHistoryReportPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const pipelineId = searchParams.get('pipelineId')

  const [steps, setSteps] = useState<{ id: number; name: string }[]>([])
  const [rows, setRows] = useState<HistoryRow[]>([])
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)

  const [filterStepId, setFilterStepId] = useState<string>('')
  const [filterPeriodStart, setFilterPeriodStart] = useState<string>('')
  const [filterPeriodEnd, setFilterPeriodEnd] = useState<string>('')

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [stepFields, setStepFields] = useState<any[]>([])
  const [expandedFieldId, setExpandedFieldId] = useState<string | number | null>(null)
  const [customFieldFilters, setCustomFieldFilters] = useState<Record<number, string>>({})
  const [entryOccurrenceFilter, setEntryOccurrenceFilter] = useState<'first' | 'last' | 'all'>('all')
  const [selectedPeriodOption, setSelectedPeriodOption] = useState<string>('')
  const [selectedDateFilterType, setSelectedDateFilterType] = useState<'first' | 'last' | null>(null)
  const [isStepAccordionOpen, setIsStepAccordionOpen] = useState(true)

  useEffect(() => {
    if (!pipelineId) return
    axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step?pipelineId=${pipelineId}`)
      .then(res => { if (res.data?.data) setSteps(res.data.data) })
      .catch(() => {})
  }, [pipelineId])

  useEffect(() => {
    if (!pipelineId || !filterStepId) {
      setStepFields([])
      return
    }
    axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/step/fields?pipelineId=${pipelineId}&stepId=${filterStepId}`)
      .then(res => {
        if (res.data?.data) {
          setStepFields(res.data.data)
        }
      })
      .catch(() => {})
  }, [pipelineId, filterStepId])

  useEffect(() => {
    setEntryOccurrenceFilter('all')
    setCustomFieldFilters({})
    setSelectedPeriodOption('')
    setSelectedDateFilterType(null)
    setExpandedFieldId(null)
  }, [filterStepId])

  const calculatePeriod = (option: string) => {
    const now = new Date()
    let start = new Date()
    let end = new Date()

    switch (option) {
      case 'hoje':
        start.setHours(0, 0, 0, 0)
        end.setHours(23, 59, 59, 999)
        break
      case 'ontem':
        start.setDate(now.getDate() - 1)
        start.setHours(0, 0, 0, 0)
        end.setDate(now.getDate() - 1)
        end.setHours(23, 59, 59, 999)
        break
      case 'esta semana': {
        const day = now.getDay()
        const diffToMonday = day === 0 ? -6 : 1 - day
        start.setDate(now.getDate() + diffToMonday)
        start.setHours(0, 0, 0, 0)
        end.setDate(start.getDate() + 6)
        end.setHours(23, 59, 59, 999)
        break
      }
      case 'semana anterior': {
        const prevMonday = new Date()
        const day2 = now.getDay()
        const diffToMonday2 = day2 === 0 ? -6 : 1 - day2
        prevMonday.setDate(now.getDate() + diffToMonday2 - 7)
        start = new Date(prevMonday)
        start.setHours(0, 0, 0, 0)
        end = new Date(prevMonday)
        end.setDate(prevMonday.getDate() + 6)
        end.setHours(23, 59, 59, 999)
        break
      }
      case 'este mês':
        start = new Date(now.getFullYear(), now.getMonth(), 1)
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)
        break
      case 'mês anterior':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        end = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999)
        break
      case 'este ano':
        start = new Date(now.getFullYear(), 0, 1)
        end = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999)
        break
      case 'ano anterior':
        start = new Date(now.getFullYear() - 1, 0, 1)
        end = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999)
        break
      default:
        return null
    }

    const format = (d: Date) => {
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    return { start: format(start), end: format(end) }
  }

  const handlePeriodChange = (type: 'first' | 'last', option: string) => {
    setSelectedDateFilterType(type)
    setSelectedPeriodOption(option)
    setEntryOccurrenceFilter(type)

    if (option === 'todas' || option === '') {
      setFilterPeriodStart('')
      setFilterPeriodEnd('')
      setEntryOccurrenceFilter('all')
      setSelectedDateFilterType(null)
      setSelectedPeriodOption('')
    } else {
      const period = calculatePeriod(option)
      if (period) {
        setFilterPeriodStart(period.start)
        setFilterPeriodEnd(period.end)
      }
    }
  }

  const handleCustomFieldChange = (fieldId: number, value: string) => {
    setCustomFieldFilters(prev => ({
      ...prev,
      [fieldId]: value
    }))
  }

  const fetchReport = useCallback(async () => {
    if (!pipelineId) return
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.set('pipelineId', pipelineId)
      if (filterStepId) params.set('stepId', filterStepId)
      if (filterPeriodStart) params.set('periodStart', filterPeriodStart)
      if (filterPeriodEnd) params.set('periodEnd', filterPeriodEnd)
      const res = await axios.get(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/step-history/report?${params.toString()}`
      )
      setRows(Array.isArray(res.data) ? res.data : (res.data?.data || []))
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [pipelineId, filterStepId, filterPeriodStart, filterPeriodEnd])

  useEffect(() => { fetchReport() }, [fetchReport])

  const clearFilters = () => {
    setFilterStepId('')
    setFilterPeriodStart('')
    setFilterPeriodEnd('')
    setEntryOccurrenceFilter('all')
    setCustomFieldFilters({})
    setSelectedPeriodOption('')
    setSelectedDateFilterType(null)
    setExpandedFieldId(null)
  }

  const hasFilters = filterStepId || filterPeriodStart || filterPeriodEnd || entryOccurrenceFilter !== 'all' || Object.values(customFieldFilters).some(v => v !== '')

  const ticketVisitCount = useMemo(() => {
    const counts: Record<number, Record<number, number>> = {}
    for (const row of rows) {
      if (!counts[row.ticketId]) counts[row.ticketId] = {}
      counts[row.ticketId][row.stepId] = (counts[row.ticketId][row.stepId] || 0) + 1
    }
    return counts
  }, [rows])

  const filteredRows = useMemo(() => {
    let result = [...rows]

    if (entryOccurrenceFilter === 'first') {
      const firstEntries: Record<number, HistoryRow> = {}
      for (const row of rows) {
        const existing = firstEntries[row.ticketId]
        if (!existing || new Date(row.enteredAt).getTime() < new Date(existing.enteredAt).getTime()) {
          firstEntries[row.ticketId] = row
        }
      }
      result = Object.values(firstEntries)
    } else if (entryOccurrenceFilter === 'last') {
      const lastEntries: Record<number, HistoryRow> = {}
      for (const row of rows) {
        const existing = lastEntries[row.ticketId]
        if (!existing || new Date(row.enteredAt).getTime() > new Date(existing.enteredAt).getTime()) {
          lastEntries[row.ticketId] = row
        }
      }
      result = Object.values(lastEntries)
    }

    Object.entries(customFieldFilters).forEach(([fieldIdStr, searchValue]) => {
      if (!searchValue || searchValue.trim() === '') return
      const fieldId = Number(fieldIdStr)
      const fieldObj = stepFields.find(f => f.PipelineField?.id === fieldId)
      const fieldName = fieldObj?.PipelineField?.name
      if (!fieldName) return

      result = result.filter(row => {
        const rowFields = row.fields || {}
        const value = rowFields[fieldName] || ''
        return value.toLowerCase().includes(searchValue.toLowerCase())
      })
    })

    return result
  }, [rows, entryOccurrenceFilter, customFieldFilters, stepFields])

  const handleExport = async () => {
    setExporting(true)
    try {
      const columns = [
        { key: 'ticketId', label: 'ID do Card', checked: true },
        { key: 'ticket.title', label: 'Título', checked: true },
        { key: 'stepName', label: 'Etapa', checked: true },
        { key: 'enteredAt', label: 'Entrada', checked: true },
        { key: 'exitedAt', label: 'Saída', checked: true },
        { key: 'durationMs', label: 'Duração (ms)', checked: true },
        { key: 'userName', label: 'Usuário', checked: true },
        { key: 'isCurrent', label: 'Atual', checked: true },
        { key: 'ticket.status', label: 'Status', checked: true },
        { key: 'ticket.createByName', label: 'Criado por', checked: true },
        { key: 'tags', label: 'Tags', checked: true },
        { key: 'stepExecutionTime', label: 'Tempo estimado (s)', checked: true },
      ]
      
      const formattedFieldFilters = Object.entries(customFieldFilters)
        .map(([fieldId, value]) => ({
          pipelineFieldId: Number(fieldId),
          value,
        }))
        .filter(ff => ff.value && ff.value.trim() !== '')

      const body: any = {
        pipelineId: pipelineId ? Number(pipelineId) : undefined,
        stepId: filterStepId ? Number(filterStepId) : undefined,
        periodStart: filterPeriodStart || undefined,
        periodEnd: filterPeriodEnd || undefined,
        fieldFilters: formattedFieldFilters,
        columns,
      }
      const response = await axios.post(
        `${process.env.REACT_APP_GATEWAY_BASE_URL}/pipelines/ticket/step-history/export`,
        body,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          },
        }
      )
      if (response.data instanceof Blob) {
        const url = window.URL.createObjectURL(response.data)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'historico-etapas.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Erro na exportação:', error)
    } finally {
      setExporting(false)
    }
  }

  const isExpired = (row: HistoryRow) => {
    if (row.stepExecutionTime && row.stepExecutionTime > 0) {
      return row.isCurrent && row.durationMs / 1000 >= row.stepExecutionTime
    }
    return false
  }

  const columns = useMemo<StandardColumn<HistoryRow>[]>(() => [
    {
      id: 'card',
      label: 'Card',
      accessor: (row) => row.ticketId,
      cell: (row) => (
        <>
          <a href={`/panel/pipeline/ticket?ticketId=${row.ticketId}${pipelineId ? `&pipelineId=${pipelineId}` : ''}`} style={{ color: '#0090D3', textDecoration: 'none', fontWeight: 600 }}>
            #{row.ticketId}
          </a>
          <span style={{ marginLeft: 6, color: '#6b7280' }}>{row.ticket.title}</span>
        </>
      ),
      exportValue: (row) => `#${row.ticketId} ${row.ticket.title}`,
    },
    {
      id: 'stepName',
      label: 'Etapa',
      accessor: (row) => row.stepName,
      cell: (row) => {
        const visitCount = ticketVisitCount[row.ticketId]?.[row.stepId] || 0
        return (
          <>
            {row.stepName}
            {visitCount > 1 && <ReworkTag>Retrabalho</ReworkTag>}
          </>
        )
      },
    },
    {
      id: 'enteredAt',
      label: 'Entrada',
      accessor: (row) => row.enteredAt ?? '',
      cell: (row) => <>{formatDateTime(row.enteredAt)}</>,
      exportValue: (row) => formatDateTime(row.enteredAt),
    },
    {
      id: 'exitedAt',
      label: 'Saída',
      accessor: (row) => row.exitedAt ?? '',
      cell: (row) => <>{formatDateTime(row.exitedAt)}</>,
      exportValue: (row) => formatDateTime(row.exitedAt),
    },
    {
      id: 'duration',
      label: 'Duração',
      accessor: (row) => row.durationMs,
      cell: (row) => {
        const expired = isExpired(row)
        return (
          <>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Clock size={12} color={expired ? '#BF383B' : '#6b7280'} />
              {formatDuration(row.durationMs)}
              {row.stepExecutionTime && row.stepExecutionTime > 0 ? (
                <span style={{ color: '#9ca3af' }}>/ {formatDuration(row.stepExecutionTime * 1000)}</span>
              ) : null}
            </span>
            {expired && <ExpiredBadge>Expirado</ExpiredBadge>}
          </>
        )
      },
      exportValue: (row) => formatDuration(row.durationMs),
    },
    {
      id: 'userName',
      label: 'Usuário',
      accessor: (row) => row.userName || '—',
    },
    {
      id: 'status',
      label: 'Status',
      accessor: (row) => (row.isCurrent ? 'Em andamento' : 'Concluído'),
      cell: (row) => (
        row.isCurrent ? (
          <span style={{ color: '#0090D3', fontWeight: 600 }}>Em andamento</span>
        ) : (
          <span style={{ color: '#6b7280' }}>Concluído</span>
        )
      ),
    },
  ], [pipelineId, ticketVisitCount])

  const { columnsState, handleColumnsChange, handleRestoreColumns } =
    useColumnsState(stepHistoryScreenKey, columns)

  return (
    <PageRoot>
      <PageHeader>
        <BackButton onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Voltar
        </BackButton>
        <PageTitle>Relatórios</PageTitle>
        <ExportButton onClick={handleExport} disabled={exporting || filteredRows.length === 0}>
          <Download size={16} /> {exporting ? 'Exportando...' : 'Exportar Excel'}
        </ExportButton>
      </PageHeader>

      <FiltersBar>
        <FilterGroup>
          <FilterLabel>Etapa</FilterLabel>
          <FilterSelect value={filterStepId} onChange={(e) => setFilterStepId(e.target.value)}>
            <option value="">Todas</option>
            {steps.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </FilterSelect>
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Período início</FilterLabel>
          <FilterInput type="date" value={filterPeriodStart} onChange={(e) => setFilterPeriodStart(e.target.value)} />
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Período fim</FilterLabel>
          <FilterInput type="date" value={filterPeriodEnd} onChange={(e) => setFilterPeriodEnd(e.target.value)} />
        </FilterGroup>
        
        <ApplyFilterButton 
          type="button" 
          disabled={!filterStepId} 
          onClick={() => setIsDrawerOpen(true)}
          style={{ 
            opacity: filterStepId ? 1 : 0.5, 
            cursor: filterStepId ? 'pointer' : 'not-allowed',
            backgroundColor: '#fff',
            color: '#6b7280',
            border: '1px solid #d1d5db'
          }}
        >
          <SlidersHorizontal size={14} /> Filtros
        </ApplyFilterButton>

        <ApplyFilterButton 
          onClick={fetchReport}
        >
          <Filter size={14} /> Filtrar
        </ApplyFilterButton>

        {hasFilters && (
          <ClearFilterButton onClick={clearFilters}>
            <X size={14} /> Limpar
          </ClearFilterButton>
        )}
      </FiltersBar>

      {loading ? (
        <LoadingState>Carregando relatório...</LoadingState>
      ) : (
        <TableWrapper>
          <StandardDataTable
            columns={columns}
            data={filteredRows}
            columnsState={columnsState}
            onColumnsChange={handleColumnsChange}
            onRestoreColumns={handleRestoreColumns}
            countLabel='registros'
            emptyMessage='Nenhum registro encontrado'
          />
        </TableWrapper>
      )}

      <DrawerOverlay $isOpen={isDrawerOpen} onClick={() => setIsDrawerOpen(false)} />
      <DrawerContent $isOpen={isDrawerOpen}>
        <DrawerHeader>
          <DrawerTitle>Filtros Avançados</DrawerTitle>
          <DrawerCloseButton onClick={() => setIsDrawerOpen(false)}>
            <X size={18} />
          </DrawerCloseButton>
        </DrawerHeader>
        <DrawerBody>
          {filterStepId && (
            <AccordionItem>
              <AccordionHeader onClick={() => setIsStepAccordionOpen(!isStepAccordionOpen)}>
                <span>{steps.find(s => s.id === Number(filterStepId))?.name || 'Etapa'}</span>
                {isStepAccordionOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </AccordionHeader>
              <AccordionContent $isOpen={isStepAccordionOpen}>
                {stepFields.map(field => {
                  const fieldId = field.PipelineField?.id
                  const fieldName = field.PipelineField?.name
                  const typeName = field.PipelineField?.typeField?.name || ''
                  const isExpanded = expandedFieldId === fieldId
                  const isSelect = typeName === 'select'
                  
                  return (
                    <FieldItem key={fieldId}>
                      <FieldLabelContainer 
                        $active={Boolean(customFieldFilters[fieldId])}
                        onClick={() => setExpandedFieldId(isExpanded ? null : fieldId)}
                      >
                        <FieldLabelText>
                          {isSelect ? <List size={16} /> : <Type size={16} />}
                          {fieldName}
                        </FieldLabelText>
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </FieldLabelContainer>
                      <FieldFilterContent $isOpen={isExpanded}>
                        <TextInput 
                          type="text"
                          placeholder={`Filtrar por ${fieldName}...`}
                          value={customFieldFilters[fieldId] || ''}
                          onChange={(e) => handleCustomFieldChange(fieldId, e.target.value)}
                        />
                      </FieldFilterContent>
                    </FieldItem>
                  )
                })}

                <FieldItem>
                  <FieldLabelContainer 
                    $active={selectedDateFilterType === 'first'}
                    onClick={() => setExpandedFieldId(expandedFieldId === 'first' ? null : 'first')}
                  >
                    <FieldLabelText>
                      <Calendar size={16} />
                      Primeira vez que entrou na fase {steps.find(s => s.id === Number(filterStepId))?.name}
                    </FieldLabelText>
                    {expandedFieldId === 'first' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </FieldLabelContainer>
                  <FieldFilterContent $isOpen={expandedFieldId === 'first'}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase' }}>Período</span>
                    <RadioGroup>
                      {[
                        { val: 'hoje', label: 'hoje' },
                        { val: 'ontem', label: 'ontem' },
                        { val: 'esta semana', label: 'esta semana' },
                        { val: 'semana anterior', label: 'semana anterior' },
                        { val: 'este mês', label: 'este mês' },
                        { val: 'mês anterior', label: 'mês anterior' },
                        { val: 'este ano', label: 'este ano' },
                        { val: 'ano anterior', label: 'ano anterior' },
                        { val: 'todas', label: 'todas (limpar)' }
                      ].map(opt => (
                        <RadioLabel key={opt.val}>
                          <RadioInput 
                            type="radio" 
                            name="first_occurrence_period"
                            checked={selectedDateFilterType === 'first' && selectedPeriodOption === opt.val}
                            onChange={() => handlePeriodChange('first', opt.val)}
                          />
                          {opt.label}
                        </RadioLabel>
                      ))}
                    </RadioGroup>
                  </FieldFilterContent>
                </FieldItem>

                <FieldItem>
                  <FieldLabelContainer 
                    $active={selectedDateFilterType === 'last'}
                    onClick={() => setExpandedFieldId(expandedFieldId === 'last' ? null : 'last')}
                  >
                    <FieldLabelText>
                      <Calendar size={16} />
                      Última vez que entrou na fase {steps.find(s => s.id === Number(filterStepId))?.name}
                    </FieldLabelText>
                    {expandedFieldId === 'last' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </FieldLabelContainer>
                  <FieldFilterContent $isOpen={expandedFieldId === 'last'}>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase' }}>Período</span>
                    <RadioGroup>
                      {[
                        { val: 'hoje', label: 'hoje' },
                        { val: 'ontem', label: 'ontem' },
                        { val: 'esta semana', label: 'esta semana' },
                        { val: 'semana anterior', label: 'semana anterior' },
                        { val: 'este mês', label: 'este mês' },
                        { val: 'mês anterior', label: 'mês anterior' },
                        { val: 'este ano', label: 'este ano' },
                        { val: 'ano anterior', label: 'ano anterior' },
                        { val: 'todas', label: 'todas (limpar)' }
                      ].map(opt => (
                        <RadioLabel key={opt.val}>
                          <RadioInput 
                            type="radio" 
                            name="last_occurrence_period"
                            checked={selectedDateFilterType === 'last' && selectedPeriodOption === opt.val}
                            onChange={() => handlePeriodChange('last', opt.val)}
                          />
                          {opt.label}
                        </RadioLabel>
                      ))}
                    </RadioGroup>
                  </FieldFilterContent>
                </FieldItem>
              </AccordionContent>
            </AccordionItem>
          )}
          <ApplyButton onClick={() => setIsDrawerOpen(false)}>
            Fechar e Aplicar
          </ApplyButton>
        </DrawerBody>
      </DrawerContent>
    </PageRoot>
  )
}

export default StepHistoryReportPage
